import { useState, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableTeamSelect } from '@/components/composite/SearchableTeamSelect';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Search } from 'lucide-react';
import {
  useGetFeedTeamsQuery,
  useGetFeedPlayersQuery,
  useGetTeamRosterStatsQuery,
} from '@/api/feed';
import { QuestionType, FixtureSelection } from '../QuestionCoachModal';
import type { FeedPlayer } from '@/api/feed/getFeedPlayers';

interface RemovablePlayerBadgeProps {
  player: FeedPlayer;
  onRemove: (playerId: string) => void;
}

function RemovablePlayerBadge({ player, onRemove }: RemovablePlayerBadgeProps) {
  const displayText = `${player.displayName}${player.jerseyNumber ? ` #${player.jerseyNumber}` : ''}`;

  return (
    <Badge variant="secondary" className="flex items-center gap-1 pr-1">
      <span>{displayText}</span>
      <button
        onClick={() => onRemove(player.externalId)}
        className="ml-1 rounded-sm opacity-70 hover:opacity-100 transition-opacity hover:bg-destructive/20"
        aria-label={`Remove ${player.displayName}`}
        type="button"
      >
        ×
      </button>
    </Badge>
  );
}

interface PlayerSelectionCardProps {
  player: FeedPlayer;
  isSelected: boolean;
  isDisabled: boolean;
  gamesPlayed?: number;
  onToggle: (playerId: string, checked: boolean) => void;
}

function PlayerSelectionCard({
  player,
  isSelected,
  isDisabled,
  gamesPlayed,
  onToggle,
}: PlayerSelectionCardProps) {
  return (
    <Card
      data-testid={`player-${player.externalId}`}
      className={`m-2 p-3 cursor-pointer hover:bg-accent ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
      onClick={() => !isDisabled && onToggle(player.externalId, !isSelected)}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={isSelected}
          disabled={isDisabled}
          onCheckedChange={checked => onToggle(player.externalId, checked === true)}
        />
        <div className="flex-1">
          <div className="font-medium">{player.displayName}</div>
          <div className="text-sm text-muted-foreground">
            {player.position}
            {player.jerseyNumber && ` #${player.jerseyNumber}`}
            {gamesPlayed != null && (
              <>
                {(player.position || player.jerseyNumber) && ' · '}
                {gamesPlayed} GP
              </>
            )}
          </div>
        </div>
        {gamesPlayed != null && (
          <Badge variant="secondary" className="shrink-0">
            {gamesPlayed} {gamesPlayed === 1 ? 'game' : 'games'}
          </Badge>
        )}
      </div>
    </Card>
  );
}

interface TeamPlayerStepProps {
  questionType?: QuestionType;
  leagueId?: string;
  fixtureId?: string;
  fixtureTeamIds?: string[]; // Team IDs from the selected fixture(s)
  selectedPlayerIds?: string[];
  selectedTeamIds?: string[];
  selectedTeamIdForPlayer?: string; // For player_over_under: team selected in previous phase
  onSelectPlayers?: (playerIds: string[], playerNames: string[]) => void;
  onSelectTeams?: (teamIds: string[], teamNames: string[]) => void;
  /**
   * Batch creation mode (DKPLAT-501 Phase 1, team_over_under only): renders one
   * free multi-select group per selected fixture instead of a single fixture's teams.
   */
  fixtures?: FixtureSelection[];
  onToggleTeamForFixture?: (fixtureId: string, teamId: string, checked: boolean) => void;
}

export function TeamPlayerStep({
  questionType,
  leagueId,
  fixtureId,
  fixtureTeamIds,
  selectedPlayerIds = [],
  selectedTeamIds = [],
  selectedTeamIdForPlayer,
  onSelectPlayers,
  onSelectTeams,
  fixtures,
  onToggleTeamForFixture,
}: TeamPlayerStepProps) {
  const [selectedTeamForPlayers, setSelectedTeamForPlayers] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'home' | 'away'>('all');

  const isPlayerQuestion =
    questionType === 'player_selection' || questionType === 'player_over_under';
  const isTeamQuestion = questionType === 'team_selection' || questionType === 'team_over_under';

  const {
    data: teams,
    isLoading: teamsLoading,
    error: teamsError,
  } = useGetFeedTeamsQuery(leagueId);

  const {
    data: players,
    isLoading: playersLoading,
    error: playersError,
  } = useGetFeedPlayersQuery(selectedTeamForPlayers);

  // Use fixture team IDs directly (passed from parent)
  const homeTeamId = fixtureTeamIds?.[0];
  const awayTeamId = fixtureTeamIds?.[1];

  // Fetch players for both teams in parallel (for enhanced player selection)
  const {
    data: homeTeamPlayers,
    isLoading: homePlayersLoading,
    error: homePlayersError,
  } = useGetFeedPlayersQuery(homeTeamId);

  const {
    data: awayTeamPlayers,
    isLoading: awayPlayersLoading,
    error: awayPlayersError,
  } = useGetFeedPlayersQuery(awayTeamId);

  const enhancedPlayersLoading = homePlayersLoading || awayPlayersLoading;
  const enhancedPlayersError = homePlayersError || awayPlayersError;

  // Fetch per-team roster stats (gamesPlayed) to order players by activity.
  // On loading/error the lookup maps stay empty, so players still render
  // (alphabetically) and no badge is shown.
  const { data: homeRosterStats } = useGetTeamRosterStatsQuery(leagueId, homeTeamId);
  const { data: awayRosterStats } = useGetTeamRosterStatsQuery(leagueId, awayTeamId);

  const homeGamesPlayedMap = useMemo(() => {
    const map = new Map<string, number>();
    homeRosterStats?.players.forEach(p => {
      if (p.gamesPlayed != null) map.set(p.id, p.gamesPlayed);
    });
    return map;
  }, [homeRosterStats]);

  const awayGamesPlayedMap = useMemo(() => {
    const map = new Map<string, number>();
    awayRosterStats?.players.forEach(p => {
      if (p.gamesPlayed != null) map.set(p.id, p.gamesPlayed);
    });
    return map;
  }, [awayRosterStats]);

  // Create maps for quick lookups
  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    teams?.forEach(team => {
      map.set(team.externalId, team.name);
    });
    return map;
  }, [teams]);

  const playerMap = useMemo(() => {
    const map = new Map<string, string>();
    // Add players from single team dropdown (legacy)
    players?.forEach(player => {
      map.set(player.externalId, player.displayName);
    });
    // Add players from both fixture teams (new enhanced UI)
    homeTeamPlayers?.forEach(player => {
      map.set(player.externalId, player.displayName);
    });
    awayTeamPlayers?.forEach(player => {
      map.set(player.externalId, player.displayName);
    });
    return map;
  }, [players, homeTeamPlayers, awayTeamPlayers]);

  // Map-based lookups for O(1) player access
  const homePlayersMap = useMemo(() => {
    const map = new Map<string, FeedPlayer>();
    homeTeamPlayers?.forEach(player => {
      map.set(player.externalId, player);
    });
    return map;
  }, [homeTeamPlayers]);

  const awayPlayersMap = useMemo(() => {
    const map = new Map<string, FeedPlayer>();
    awayTeamPlayers?.forEach(player => {
      map.set(player.externalId, player);
    });
    return map;
  }, [awayTeamPlayers]);

  // Sort players by gamesPlayed (desc) so the most active players surface first;
  // players with no gamesPlayed data (e.g. soccer) fall back to alphabetical.
  const sortByGamesPlayedThenName = (players: FeedPlayer[], statsMap: Map<string, number>) =>
    [...players].sort((a, b) => {
      const gpA = statsMap.get(a.externalId) ?? -1;
      const gpB = statsMap.get(b.externalId) ?? -1;
      if (gpB !== gpA) return gpB - gpA;
      return a.displayName.localeCompare(b.displayName);
    });

  const sortedHomeTeamPlayers = useMemo(() => {
    if (!homeTeamPlayers) return [];
    return sortByGamesPlayedThenName(homeTeamPlayers, homeGamesPlayedMap);
  }, [homeTeamPlayers, homeGamesPlayedMap]);

  const sortedAwayTeamPlayers = useMemo(() => {
    if (!awayTeamPlayers) return [];
    return sortByGamesPlayedThenName(awayTeamPlayers, awayGamesPlayedMap);
  }, [awayTeamPlayers, awayGamesPlayedMap]);

  // Search-filtered players for enhanced UI
  const filteredHomeTeamPlayers = useMemo(() => {
    if (!searchQuery) return sortedHomeTeamPlayers;
    const query = searchQuery.toLowerCase().trim();
    return sortedHomeTeamPlayers.filter(player => player.displayName.toLowerCase().includes(query));
  }, [sortedHomeTeamPlayers, searchQuery]);

  const filteredAwayTeamPlayers = useMemo(() => {
    if (!searchQuery) return sortedAwayTeamPlayers;
    const query = searchQuery.toLowerCase().trim();
    return sortedAwayTeamPlayers.filter(player => player.displayName.toLowerCase().includes(query));
  }, [sortedAwayTeamPlayers, searchQuery]);

  // Grouped selected players by team for enhanced UI (O(n+m) with Maps)
  const selectedPlayersByTeam = useMemo(() => {
    const home: FeedPlayer[] = [];
    const away: FeedPlayer[] = [];

    selectedPlayerIds.forEach(playerId => {
      const homePlayer = homePlayersMap.get(playerId); // O(1)
      const awayPlayer = awayPlayersMap.get(playerId); // O(1)

      if (homePlayer) home.push(homePlayer);
      if (awayPlayer) away.push(awayPlayer);
    });

    return { home, away };
  }, [selectedPlayerIds, homePlayersMap, awayPlayersMap]);

  // Get team names for display in enhanced UI
  const homeTeamName = useMemo(() => {
    return teams?.find(t => t.externalId === homeTeamId)?.name || 'Home Team';
  }, [teams, homeTeamId]);

  const awayTeamName = useMemo(() => {
    return teams?.find(t => t.externalId === awayTeamId)?.name || 'Away Team';
  }, [teams, awayTeamId]);

  // Extract team IDs and names from fixture (for team selection)
  const fixtureHomeTeamId = fixtureTeamIds?.[0];
  const fixtureAwayTeamId = fixtureTeamIds?.[1];

  const fixtureHomeTeamName = useMemo(() => {
    return teams?.find(t => t.externalId === fixtureHomeTeamId)?.name || 'Home Team';
  }, [teams, fixtureHomeTeamId]);

  const fixtureAwayTeamName = useMemo(() => {
    return teams?.find(t => t.externalId === fixtureAwayTeamId)?.name || 'Away Team';
  }, [teams, fixtureAwayTeamId]);

  // Filter teams to only show the 2 teams from the selected fixture
  const fixtureTeams = useMemo(() => {
    if (!fixtureHomeTeamId || !fixtureAwayTeamId || !teams) {
      return teams || [];
    }
    return teams.filter(
      team => team.externalId === fixtureHomeTeamId || team.externalId === fixtureAwayTeamId
    );
  }, [teams, fixtureHomeTeamId, fixtureAwayTeamId]);

  const handleTeamToggle = (teamId: string, checked: boolean) => {
    let newTeamIds: string[];
    if (checked) {
      // For team_over_under, only allow exactly 1 team (replace any existing selection)
      if (questionType === 'team_over_under') {
        newTeamIds = [teamId];
      } else {
        newTeamIds = [...selectedTeamIds, teamId];
      }
    } else {
      newTeamIds = selectedTeamIds.filter(id => id !== teamId);
    }
    const teamNames = newTeamIds.map(id => teamMap.get(id) || id);
    onSelectTeams?.(newTeamIds, teamNames);
  };

  const handlePlayerToggle = (playerId: string, checked: boolean) => {
    let newPlayerIds: string[];
    if (checked) {
      // For player_over_under, only allow exactly 1 player (replace any existing selection)
      if (questionType === 'player_over_under') {
        newPlayerIds = [playerId];
      } else {
        newPlayerIds = [...selectedPlayerIds, playerId];
      }
    } else {
      newPlayerIds = selectedPlayerIds.filter(id => id !== playerId);
    }
    const playerNames = newPlayerIds.map(id => playerMap.get(id) || id);
    onSelectPlayers?.(newPlayerIds, playerNames);
  };

  // Batch creation mode (DKPLAT-501 Phase 1, team_over_under only): one free
  // multi-select group per selected fixture.
  if (fixtures) {
    if (teamsLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading teams...</span>
        </div>
      );
    }

    if (teamsError) {
      return (
        <Alert variant="destructive">
          <AlertDescription>Failed to load teams. Please try again.</AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <Label className="text-base mb-2 block">Select teams for each fixture</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Choose one or more teams per fixture — a separate question will be created for each team
            selected.
          </p>
        </div>

        {fixtures.map(fixture => (
          <div key={fixture.fixtureId} className="space-y-2">
            <Label className="text-sm font-semibold">{fixture.fixtureDisplay}</Label>
            <div className="space-y-2">
              {fixture.fixtureTeamIds.map(teamId => {
                const teamName = teamMap.get(teamId) || teamId;
                const isSelected = fixture.selectedTeamIds.includes(teamId);

                return (
                  <Card
                    key={teamId}
                    data-testid={`team-${fixture.fixtureId}-${teamId}`}
                    className={`m-2 p-3 cursor-pointer hover:bg-accent ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                    onClick={() => onToggleTeamForFixture?.(fixture.fixtureId, teamId, !isSelected)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={checked =>
                          onToggleTeamForFixture?.(fixture.fixtureId, teamId, checked === true)
                        }
                        // Stop the click from bubbling to the Card's onClick,
                        // which would toggle the team straight back off.
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="font-medium flex-1">{teamName}</span>
                    </div>
                  </Card>
                );
              })}
            </div>
            {fixture.selectedTeamIds.length === 0 && (
              <Alert>
                <AlertDescription>Select at least one team for this fixture.</AlertDescription>
              </Alert>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (!leagueId) {
    return (
      <Alert>
        <AlertDescription>Please select a league first.</AlertDescription>
      </Alert>
    );
  }

  if (teamsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading teams...</span>
      </div>
    );
  }

  if (teamsError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load teams. Please try again.</AlertDescription>
      </Alert>
    );
  }

  // Validate fixture team IDs for enhanced player selection (skip for individual sports like PGA)
  const isPGARound = fixtureId?.includes(':round_');
  if (isPlayerQuestion && fixtureId && fixtureTeamIds && !isPGARound) {
    if (!Array.isArray(fixtureTeamIds) || fixtureTeamIds.length !== 2) {
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Invalid fixture data: Expected 2 teams but received {fixtureTeamIds?.length || 0}.
            Please select a different fixture or contact support.
          </AlertDescription>
        </Alert>
      );
    }

    if (!fixtureTeamIds[0] || !fixtureTeamIds[1]) {
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Invalid fixture data: Team IDs are missing. Please select a different fixture or contact
            support.
          </AlertDescription>
        </Alert>
      );
    }
  }

  // Validate fixture team IDs for team selection
  if (isTeamQuestion && fixtureId && fixtureTeamIds) {
    if (!Array.isArray(fixtureTeamIds) || fixtureTeamIds.length !== 2) {
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Invalid fixture data: Expected 2 teams but received {fixtureTeamIds?.length || 0}.
            Please select a different fixture or contact support.
          </AlertDescription>
        </Alert>
      );
    }

    if (!fixtureTeamIds[0] || !fixtureTeamIds[1]) {
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Invalid fixture data: Team IDs are missing. Please select a different fixture or contact
            support.
          </AlertDescription>
        </Alert>
      );
    }
  }

  // Team Selection UI
  if (isTeamQuestion) {
    const teamsToDisplay = fixtureId && fixtureTeamIds ? fixtureTeams : teams;

    return (
      <div className="space-y-4">
        <div>
          <Label className="text-base mb-2 block">
            {questionType === 'team_over_under'
              ? `Select Team from ${fixtureHomeTeamName} vs ${fixtureAwayTeamName} (1 team required)`
              : fixtureId && fixtureTeamIds
                ? `Select Teams from ${fixtureHomeTeamName} vs ${fixtureAwayTeamName} (2 teams required)`
                : 'Select Teams (minimum 2 required)'}
          </Label>
          <p className="text-sm text-muted-foreground mb-4">
            {questionType === 'team_over_under'
              ? 'Choose exactly one team for this over/under question'
              : fixtureId && fixtureTeamIds
                ? 'Choose the 2 teams from the selected fixture'
                : 'Choose at least 2 teams for the question'}
          </p>
        </div>

        <div className="space-y-2 max-h-[400px]">
          {teamsToDisplay?.map(team => {
            const isSelected = selectedTeamIds.includes(team.externalId);
            const isOtherTeamSelected =
              questionType === 'team_over_under' && selectedTeamIds.length > 0 && !isSelected;

            return (
              <Card
                key={team.externalId}
                data-testid={`team-${team.externalId}`}
                className={`m-2 p-3 cursor-pointer hover:bg-accent ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                onClick={() =>
                  !isOtherTeamSelected &&
                  handleTeamToggle(team.externalId, !selectedTeamIds.includes(team.externalId))
                }
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={isSelected}
                    disabled={isOtherTeamSelected}
                    onCheckedChange={checked => handleTeamToggle(team.externalId, checked === true)}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{team.name}</div>
                    {team.abbreviation && (
                      <div className="text-sm text-muted-foreground">{team.abbreviation}</div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {selectedTeamIds.length > 0 && (
          <Alert>
            <AlertDescription>
              {selectedTeamIds.length} team{selectedTeamIds.length !== 1 ? 's' : ''} selected
            </AlertDescription>
          </Alert>
        )}

        {questionType === 'team_over_under' && selectedTeamIds.length === 0 && (
          <Alert>
            <AlertDescription>Please select exactly one team to continue.</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  // PGA Player Selection UI (individual sport — no teams, show all players)
  if (isPlayerQuestion && isPGARound) {
    const tournamentId = fixtureTeamIds?.[0]; // Tournament ID passed as the single "team"
    const { data: pgaPlayers, isLoading: pgaPlayersLoading } = useGetFeedPlayersQuery(tournamentId);

    const filteredPgaPlayers = useMemo(() => {
      if (!pgaPlayers) return [];
      const sorted = [...pgaPlayers].sort((a, b) => a.displayName.localeCompare(b.displayName));
      if (!searchQuery) return sorted;
      const query = searchQuery.toLowerCase().trim();
      return sorted.filter(player => player.displayName.toLowerCase().includes(query));
    }, [pgaPlayers, searchQuery]);

    return (
      <div className="space-y-4">
        <div>
          <Label className="text-base mb-2 block">
            {questionType === 'player_over_under'
              ? 'Select Player (1 required)'
              : 'Select Players (minimum 2 required)'}
          </Label>
          <p className="text-sm text-muted-foreground mb-4">
            {questionType === 'player_over_under'
              ? 'Choose one golfer for this over/under question'
              : 'Choose at least 2 golfers to compare'}
          </p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search golfers by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {selectedPlayerIds.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedPlayerIds.map(playerId => {
              const player = pgaPlayers?.find(p => p.externalId === playerId);
              if (!player) return null;
              return (
                <RemovablePlayerBadge
                  key={playerId}
                  player={player}
                  onRemove={id => {
                    const newIds = selectedPlayerIds.filter(pid => pid !== id);
                    const newNames = newIds.map(pid => playerMap.get(pid) || pid);
                    onSelectPlayers?.(newIds, newNames);
                  }}
                />
              );
            })}
          </div>
        )}

        {pgaPlayersLoading ? (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading golfers...</span>
          </div>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {filteredPgaPlayers.map(player => {
              const isSelected = selectedPlayerIds.includes(player.externalId);
              const isOtherSelected =
                questionType === 'player_over_under' && selectedPlayerIds.length > 0 && !isSelected;

              return (
                <Card
                  key={player.externalId}
                  data-testid={`player-${player.externalId}`}
                  className={`mx-2 p-3 cursor-pointer hover:bg-accent ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                  onClick={() =>
                    !isOtherSelected && handlePlayerToggle(player.externalId, !isSelected)
                  }
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      disabled={isOtherSelected}
                      onCheckedChange={checked =>
                        handlePlayerToggle(player.externalId, checked === true)
                      }
                    />
                    <div className="flex-1">
                      <div className="font-medium">{player.displayName}</div>
                      {player.position && (
                        <div className="text-sm text-muted-foreground">{player.position}</div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Enhanced Player Selection UI (with fixture-based team filtering)
  if (isPlayerQuestion && fixtureId && fixtureTeamIds) {
    // For player_over_under, filter to only show players from the selected team
    const displayHomeTeamPlayers = selectedTeamIdForPlayer
      ? selectedTeamIdForPlayer === homeTeamId
        ? filteredHomeTeamPlayers
        : []
      : filteredHomeTeamPlayers;

    const displayAwayTeamPlayers = selectedTeamIdForPlayer
      ? selectedTeamIdForPlayer === awayTeamId
        ? filteredAwayTeamPlayers
        : []
      : filteredAwayTeamPlayers;

    const selectedTeamName = selectedTeamIdForPlayer
      ? selectedTeamIdForPlayer === homeTeamId
        ? homeTeamName
        : awayTeamName
      : null;

    return (
      <div className="space-y-4">
        {/* Header */}
        <div>
          <Label className="text-base mb-2 block">
            {questionType === 'player_over_under'
              ? `Select Player from ${selectedTeamName} (1 player required)`
              : 'Select Players (minimum 2 required)'}
          </Label>
          <p className="text-sm text-muted-foreground mb-4">
            {questionType === 'player_over_under'
              ? `Choose exactly one player from ${selectedTeamName} for this over/under question`
              : `Choose at least 2 players from ${homeTeamName} vs ${awayTeamName}`}
          </p>
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search players by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Loading State */}
        {enhancedPlayersLoading && (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading players...</span>
          </div>
        )}

        {/* Error State */}
        {enhancedPlayersError && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>Failed to load players. Please try again.</AlertDescription>
          </Alert>
        )}

        {/* Empty-roster state — some teams have no player data in the feed
            (e.g. smaller soccer national sides). Surface it clearly instead of
            an empty list so the admin knows to use a team question. */}
        {!enhancedPlayersLoading &&
          !enhancedPlayersError &&
          (() => {
            const homeEmpty = !!homeTeamId && (homeTeamPlayers?.length ?? 0) === 0;
            const awayEmpty = !!awayTeamId && (awayTeamPlayers?.length ?? 0) === 0;
            if (homeEmpty && awayEmpty) {
              return (
                <Alert className="mt-4">
                  <AlertDescription>
                    No player data is available for {homeTeamName} or {awayTeamName} from the data
                    feed, so player questions aren&apos;t available for this fixture. Create a
                    team-based question instead.
                  </AlertDescription>
                </Alert>
              );
            }
            if (homeEmpty || awayEmpty) {
              const missing = homeEmpty ? homeTeamName : awayTeamName;
              const available = homeEmpty ? awayTeamName : homeTeamName;
              return (
                <Alert className="mt-4">
                  <AlertDescription>
                    No player data is available for {missing} from the data feed — only {available}{' '}
                    players can be selected for this question.
                  </AlertDescription>
                </Alert>
              );
            }
            return null;
          })()}

        {/* Simple List for player_over_under (single team) */}
        {selectedTeamIdForPlayer && !enhancedPlayersLoading && !enhancedPlayersError && (
          <div className="space-y-2 max-h-[300px] overflow-y-auto mt-4">
            {displayHomeTeamPlayers.length > 0 &&
              displayHomeTeamPlayers.map(player => (
                <PlayerSelectionCard
                  key={player.externalId}
                  player={player}
                  isSelected={selectedPlayerIds.includes(player.externalId)}
                  isDisabled={
                    questionType === 'player_over_under' &&
                    selectedPlayerIds.length > 0 &&
                    !selectedPlayerIds.includes(player.externalId)
                  }
                  gamesPlayed={homeGamesPlayedMap.get(player.externalId)}
                  onToggle={handlePlayerToggle}
                />
              ))}
            {displayAwayTeamPlayers.length > 0 &&
              displayAwayTeamPlayers.map(player => (
                <PlayerSelectionCard
                  key={player.externalId}
                  player={player}
                  isSelected={selectedPlayerIds.includes(player.externalId)}
                  isDisabled={
                    questionType === 'player_over_under' &&
                    selectedPlayerIds.length > 0 &&
                    !selectedPlayerIds.includes(player.externalId)
                  }
                  gamesPlayed={awayGamesPlayedMap.get(player.externalId)}
                  onToggle={handlePlayerToggle}
                />
              ))}
            {displayHomeTeamPlayers.length === 0 && displayAwayTeamPlayers.length === 0 && (
              <Alert>
                <AlertDescription>
                  {searchQuery
                    ? `No players found matching "${searchQuery}"`
                    : 'No players available for this team'}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Tabs - Only for player_selection (multiple teams) */}
        {!selectedTeamIdForPlayer && !enhancedPlayersLoading && !enhancedPlayersError && (
          <Tabs value={activeTab} onValueChange={val => setActiveTab(val as typeof activeTab)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">
                All Players (
                {(displayHomeTeamPlayers?.length || 0) + (displayAwayTeamPlayers?.length || 0)})
              </TabsTrigger>
              <TabsTrigger value="home">
                {homeTeamName} ({displayHomeTeamPlayers?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="away">
                {awayTeamName} ({displayAwayTeamPlayers?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* All Players Tab */}
            <TabsContent value="all" className="space-y-2 max-h-[300px] overflow-y-auto mt-4">
              {displayHomeTeamPlayers.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2 sticky top-0 bg-background">
                    {homeTeamName}
                  </div>
                  {displayHomeTeamPlayers.map(player => (
                    <PlayerSelectionCard
                      key={player.externalId}
                      player={player}
                      isSelected={selectedPlayerIds.includes(player.externalId)}
                      isDisabled={
                        questionType === 'player_over_under' &&
                        selectedPlayerIds.length > 0 &&
                        !selectedPlayerIds.includes(player.externalId)
                      }
                      gamesPlayed={homeGamesPlayedMap.get(player.externalId)}
                      onToggle={handlePlayerToggle}
                    />
                  ))}
                </div>
              )}

              {displayAwayTeamPlayers.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2 mt-4 sticky top-0 bg-background">
                    {awayTeamName}
                  </div>
                  {displayAwayTeamPlayers.map(player => (
                    <PlayerSelectionCard
                      key={player.externalId}
                      player={player}
                      isSelected={selectedPlayerIds.includes(player.externalId)}
                      isDisabled={
                        questionType === 'player_over_under' &&
                        selectedPlayerIds.length > 0 &&
                        !selectedPlayerIds.includes(player.externalId)
                      }
                      gamesPlayed={awayGamesPlayedMap.get(player.externalId)}
                      onToggle={handlePlayerToggle}
                    />
                  ))}
                </div>
              )}

              {displayHomeTeamPlayers.length === 0 && displayAwayTeamPlayers.length === 0 && (
                <Alert>
                  <AlertDescription>No players found matching "{searchQuery}"</AlertDescription>
                </Alert>
              )}
            </TabsContent>

            {/* Home Team Tab */}
            <TabsContent value="home" className="space-y-2 max-h-[300px] overflow-y-auto mt-4">
              {displayHomeTeamPlayers.length > 0 ? (
                displayHomeTeamPlayers.map(player => (
                  <PlayerSelectionCard
                    key={player.externalId}
                    player={player}
                    isSelected={selectedPlayerIds.includes(player.externalId)}
                    isDisabled={
                      questionType === 'player_over_under' &&
                      selectedPlayerIds.length > 0 &&
                      !selectedPlayerIds.includes(player.externalId)
                    }
                    gamesPlayed={homeGamesPlayedMap.get(player.externalId)}
                    onToggle={handlePlayerToggle}
                  />
                ))
              ) : (
                <Alert>
                  <AlertDescription>
                    {searchQuery
                      ? `No players found matching "${searchQuery}"`
                      : 'No players available for this team'}
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            {/* Away Team Tab */}
            <TabsContent value="away" className="space-y-2 max-h-[300px] overflow-y-auto mt-4">
              {displayAwayTeamPlayers.length > 0 ? (
                displayAwayTeamPlayers.map(player => (
                  <PlayerSelectionCard
                    key={player.externalId}
                    player={player}
                    isSelected={selectedPlayerIds.includes(player.externalId)}
                    isDisabled={
                      questionType === 'player_over_under' &&
                      selectedPlayerIds.length > 0 &&
                      !selectedPlayerIds.includes(player.externalId)
                    }
                    gamesPlayed={awayGamesPlayedMap.get(player.externalId)}
                    onToggle={handlePlayerToggle}
                  />
                ))
              ) : (
                <Alert>
                  <AlertDescription>
                    {searchQuery
                      ? `No players found matching "${searchQuery}"`
                      : 'No players available for this team'}
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Grouped Selection Display */}
        {(selectedPlayersByTeam.home.length > 0 || selectedPlayersByTeam.away.length > 0) && (
          <div className="space-y-3 pt-4 border-t">
            <div className="font-medium text-sm">Selected Players ({selectedPlayerIds.length})</div>

            {selectedPlayersByTeam.home.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground mb-2">{homeTeamName}</div>
                <div className="flex flex-wrap gap-2">
                  {selectedPlayersByTeam.home.map(player => (
                    <RemovablePlayerBadge
                      key={player.externalId}
                      player={player}
                      onRemove={id => handlePlayerToggle(id, false)}
                    />
                  ))}
                </div>
              </div>
            )}

            {selectedPlayersByTeam.away.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground mb-2">{awayTeamName}</div>
                <div className="flex flex-wrap gap-2">
                  {selectedPlayersByTeam.away.map(player => (
                    <RemovablePlayerBadge
                      key={player.externalId}
                      player={player}
                      onRemove={id => handlePlayerToggle(id, false)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Validation message for player_over_under */}
        {questionType === 'player_over_under' && selectedPlayerIds.length === 0 && (
          <Alert>
            <AlertDescription>Please select exactly one player to continue.</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  // Player Selection UI
  if (isPlayerQuestion) {
    return (
      <div className="space-y-4">
        <div>
          <Label className="text-base mb-2 block">Select Players (minimum 2 required)</Label>
          <p className="text-sm text-muted-foreground mb-4">
            First select a team, then choose at least 2 players
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="team-select">Team</Label>
          <SearchableTeamSelect
            teams={teams || []}
            value={selectedTeamForPlayers || ''}
            onValueChange={setSelectedTeamForPlayers}
            placeholder="Select a team to view players"
            id="team-select"
          />
        </div>

        {selectedTeamForPlayers && (
          <>
            {playersLoading ? (
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading players...</span>
              </div>
            ) : playersError ? (
              <Alert variant="destructive">
                <AlertDescription>Failed to load players. Please try again.</AlertDescription>
              </Alert>
            ) : players && players.length === 0 ? (
              <Alert>
                <AlertDescription>No players found for this team.</AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {players?.map(player => (
                  <Card
                    key={player.externalId}
                    className="m-2 p-3 cursor-pointer hover:bg-accent"
                    onClick={() =>
                      handlePlayerToggle(
                        player.externalId,
                        !selectedPlayerIds.includes(player.externalId)
                      )
                    }
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedPlayerIds.includes(player.externalId)}
                        onCheckedChange={checked =>
                          handlePlayerToggle(player.externalId, checked === true)
                        }
                      />
                      <div className="flex-1">
                        <div className="font-medium">{player.displayName}</div>
                        <div className="text-sm text-muted-foreground">
                          {player.position}
                          {player.jerseyNumber && ` #${player.jerseyNumber}`}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {selectedPlayerIds.length > 0 && (
          <Alert>
            <AlertDescription>
              {selectedPlayerIds.length} player{selectedPlayerIds.length !== 1 ? 's' : ''} selected
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <Alert variant="destructive">
      <AlertDescription>Invalid question type.</AlertDescription>
    </Alert>
  );
}
